import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Calendar, Clock, Tag, User, Phone, Mail, Hash, Trash2, MessageSquare, Paperclip, Download, Save, Loader2, AlertCircle, CheckCircle2, X, Bell, BellRing, IdCard } from "lucide-react";
import { api } from "../lib/api";
import AppHeader from "../components/AppHeader";

const STATUSES = [
  { key: "sin_empezar", label: "Sin empezar" },
  { key: "en_progreso", label: "En progreso" },
  { key: "preparado_subasta", label: "Preparado para subasta" },
  { key: "pendiente_gestion", label: "Pendiente de gestión" },
  { key: "terminado", label: "Terminado" },
];
const PRIORITIES = [{ key: "baja", label: "Baja" }, { key: "media", label: "Media" }, { key: "alta", label: "Alta" }];

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;
const toDtLocal = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch { return ""; }
};
const fromDtLocal = (v) => v ? new Date(v).toISOString() : null;

export default function CRMCardDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [card, setCard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [tagInput, setTagInput] = useState("");
  const [comment, setComment] = useState("");
  const [uploading, setUploading] = useState(false);

  const fetchCard = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/crm/cards/${id}`);
      setCard(data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Error cargando tarjeta");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { fetchCard(); }, [id]);

  const patch = async (changes) => {
    setSaving(true); setError(""); setSuccess("");
    try {
      const { data } = await api.patch(`/admin/crm/cards/${id}`, changes);
      setCard(data);
      setSuccess("Guardado");
      setTimeout(() => setSuccess(""), 1500);
    } catch (e) {
      setError(e?.response?.data?.detail || "Error guardando");
    } finally {
      setSaving(false);
    }
  };

  const addTag = async () => {
    const t = tagInput.trim();
    if (!t) return;
    const tags = Array.from(new Set([...(card.tags || []), t]));
    await patch({ tags });
    setTagInput("");
  };
  const removeTag = async (t) => {
    const tags = (card.tags || []).filter((x) => x !== t);
    await patch({ tags });
  };

  const submitComment = async () => {
    if (!comment.trim()) return;
    try {
      await api.post(`/admin/crm/cards/${id}/comments`, { text: comment.trim() });
      setComment("");
      fetchCard();
    } catch (e) { setError("No se pudo guardar el comentario"); }
  };
  const delComment = async (cid) => {
    if (!window.confirm("¿Borrar comentario?")) return;
    try {
      await api.delete(`/admin/crm/cards/${id}/comments/${cid}`);
      fetchCard();
    } catch { setError("Error borrando"); }
  };

  const uploadFile = async (file) => {
    if (!file) return;
    setUploading(true); setError("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/admin/crm/cards/${id}/attachments`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      fetchCard();
    } catch (e) {
      setError(e?.response?.data?.detail || "Error subiendo archivo");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };
  const delAttachment = async (aid) => {
    if (!window.confirm("¿Borrar adjunto?")) return;
    try {
      await api.delete(`/admin/crm/cards/${id}/attachments/${aid}`);
      fetchCard();
    } catch { setError("Error borrando adjunto"); }
  };

  // -------- Alertas --------
  const addAlerta = async (dt, msg) => {
    if (!dt) return;
    try {
      const fechaIso = new Date(dt).toISOString();
      await api.post(`/admin/crm/cards/${id}/alertas`, { fecha_hora: fechaIso, mensaje: msg || "" });
      fetchCard();
    } catch (e) {
      setError(e?.response?.data?.detail || "Error añadiendo alerta");
    }
  };
  const delAlerta = async (aid) => {
    if (!window.confirm("¿Borrar esta alerta?")) return;
    try {
      await api.delete(`/admin/crm/cards/${id}/alertas/${aid}`);
      fetchCard();
    } catch { setError("Error borrando alerta"); }
  };

  const downloadAttachment = (aid) => {
    const token = localStorage.getItem("fm_token");
    const url = `${API_BASE}/admin/crm/cards/${id}/attachments/${aid}`;
    // Open in new tab; backend uses session cookie. Use fetch to get blob if needed.
    fetch(url, { credentials: "include", headers: token ? { Authorization: `Bearer ${token}` } : {} })
      .then((r) => r.blob().then((b) => ({ blob: b, headers: r.headers })))
      .then(({ blob, headers }) => {
        const cd = headers.get("Content-Disposition") || "";
        const m = /filename="([^"]+)"/.exec(cd);
        const filename = m ? m[1] : "adjunto";
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = filename;
        document.body.appendChild(a); a.click(); a.remove();
      })
      .catch(() => setError("Error descargando"));
  };

  const deleteCard = async () => {
    if (!window.confirm("¿Eliminar esta tarjeta? No se puede deshacer.")) return;
    try {
      await api.delete(`/admin/crm/cards/${id}`);
      navigate("/admin/crm");
    } catch { setError("Error eliminando"); }
  };

  if (loading || !card) {
    return (
      <div className="min-h-screen bg-stone-950 text-stone-100">
        <AppHeader />
        <div className="text-center py-20"><Loader2 className="w-6 h-6 animate-spin text-[#E0651F] mx-auto" /></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-950 text-stone-100">
      <AppHeader />

      <div className="border-b border-stone-800 bg-stone-900 px-4 sm:px-6 py-3 sm:py-4">
        <div className="max-w-5xl mx-auto flex items-center gap-3">
          <Link to="/admin/crm" className="text-stone-400 hover:text-white inline-flex items-center gap-1 text-xs uppercase tracking-[0.18em] font-bold" data-testid="back-to-crm">
            <ArrowLeft className="w-4 h-4" /> Volver
          </Link>
          <span className="ml-auto flex items-center gap-3 text-xs text-stone-500">
            {saving && <span className="inline-flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Guardando…</span>}
            {success && <span className="text-emerald-400 inline-flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> {success}</span>}
          </span>
          <button onClick={deleteCard} className="text-red-400 hover:text-red-300 text-xs uppercase tracking-[0.18em] font-bold inline-flex items-center gap-1" data-testid="delete-card">
            <Trash2 className="w-3.5 h-3.5" /> <span className="hidden sm:inline">Eliminar</span>
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-900/50 text-red-200 border-b border-red-700 px-6 py-2 text-sm flex items-center gap-2">
          <AlertCircle className="w-4 h-4" /> {error}
        </div>
      )}

      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <input
          value={card.title}
          onChange={(e) => setCard({ ...card, title: e.target.value })}
          onBlur={(e) => e.target.value !== card.title || patch({ title: e.target.value })}
          className="w-full bg-transparent border-0 font-display text-2xl sm:text-3xl lg:text-4xl font-black tracking-tight text-white mb-6 sm:mb-8 focus:outline-none focus:bg-stone-900 px-2 py-1"
          data-testid="card-title"
        />

        <div className="grid lg:grid-cols-3 gap-8">
          {/* COLUMNA IZQUIERDA — campos */}
          <div className="lg:col-span-1 space-y-4 bg-stone-900 border border-stone-800 p-5 rounded-sm">
            <PropRow icon={<User className="w-4 h-4" />} label="Nombre cliente">
              <input value={card.client_name || ""} onChange={(e) => setCard({ ...card, client_name: e.target.value })}
                onBlur={(e) => patch({ client_name: e.target.value })}
                className="field" data-testid="field-client-name" />
            </PropRow>
            <PropRow icon={<Mail className="w-4 h-4" />} label="Email">
              <input value={card.client_email || ""} onChange={(e) => setCard({ ...card, client_email: e.target.value })}
                onBlur={(e) => patch({ client_email: e.target.value })}
                className="field" data-testid="field-client-email" />
            </PropRow>
            <PropRow icon={<Phone className="w-4 h-4" />} label="Teléfono">
              <input value={card.client_phone || ""} onChange={(e) => setCard({ ...card, client_phone: e.target.value })}
                onBlur={(e) => patch({ client_phone: e.target.value })}
                className="field" data-testid="field-client-phone" />
            </PropRow>
            <PropRow icon={<IdCard className="w-4 h-4" />} label="NIF / DNI">
              <input value={card.client_nif || ""} onChange={(e) => setCard({ ...card, client_nif: e.target.value })}
                onBlur={(e) => patch({ client_nif: e.target.value.trim().toUpperCase() })}
                placeholder="Ej: 12345678A"
                className="field font-mono text-sm uppercase" data-testid="field-client-nif" />
            </PropRow>
            <PropRow icon={<Hash className="w-4 h-4" />} label="ID Subasta (BOE)">
              <input value={card.auction_id || ""} onChange={(e) => setCard({ ...card, auction_id: e.target.value })}
                onBlur={(e) => patch({ auction_id: e.target.value })}
                className="field font-mono text-sm" data-testid="field-auction-id" />
            </PropRow>
            <PropRow label="Estado">
              <select value={card.status} onChange={(e) => patch({ status: e.target.value })}
                className="field" data-testid="field-status">
                {STATUSES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
              </select>
            </PropRow>
            <PropRow label="Prioridad">
              <select value={card.priority} onChange={(e) => patch({ priority: e.target.value })}
                className="field" data-testid="field-priority">
                {PRIORITIES.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
              </select>
            </PropRow>
            <PropRow icon={<Calendar className="w-4 h-4" />} label="Fecha entrada">
              <input type="date" value={card.entry_date || ""} onChange={(e) => patch({ entry_date: e.target.value })}
                className="field" data-testid="field-entry-date" />
            </PropRow>
            <PropRow icon={<Clock className="w-4 h-4" />} label="Fecha subasta">
              <input type="datetime-local" value={toDtLocal(card.auction_date)}
                onChange={(e) => patch({ auction_date: fromDtLocal(e.target.value) })}
                className="field" data-testid="field-auction-date" />
            </PropRow>
            <PropRow icon={<Calendar className="w-4 h-4" />} label="Próximo contacto">
              <input type="datetime-local" value={toDtLocal(card.next_contact)}
                onChange={(e) => patch({ next_contact: fromDtLocal(e.target.value) })}
                className="field" data-testid="field-next-contact" />
            </PropRow>
            <PropRow icon={<Tag className="w-4 h-4" />} label="Etiquetas">
              <div className="flex flex-wrap gap-1.5 mb-2">
                {(card.tags || []).map((t) => (
                  <span key={t} className="text-[10px] uppercase tracking-wide font-bold bg-stone-700 text-stone-200 px-2 py-1 rounded-sm inline-flex items-center gap-1">
                    {t} <button onClick={() => removeTag(t)} className="hover:text-red-400"><X className="w-3 h-3" /></button>
                  </span>
                ))}
              </div>
              <div className="flex gap-2">
                <input value={tagInput} onChange={(e) => setTagInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addTag())}
                  placeholder="Añadir..." className="field" data-testid="field-tag-input" />
                <button onClick={addTag} className="bg-[#E0651F] hover:bg-[#ff7a30] text-white px-3 py-1.5 text-xs uppercase tracking-wide font-bold rounded-sm" data-testid="field-tag-add">+</button>
              </div>
            </PropRow>
          </div>

          {/* COLUMNA DERECHA — descripcion, comentarios, adjuntos */}
          <div className="lg:col-span-2 space-y-6">
            <Section title="Descripción">
              <textarea value={card.description || ""} onChange={(e) => setCard({ ...card, description: e.target.value })}
                onBlur={(e) => patch({ description: e.target.value })}
                rows={6} className="field font-mono text-sm resize-none" data-testid="field-description"
                placeholder="Notas, contexto, particularidades del caso..." />
            </Section>

            <Section title={`Comentarios (${(card.comments || []).length})`}>
              <div className="space-y-3 mb-4">
                {(card.comments || []).map((c) => (
                  <div key={c.id} className="bg-stone-900 border-l-2 border-[#E0651F] p-3 rounded-sm" data-testid={`comment-${c.id}`}>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-bold text-xs text-white">{c.author}</span>
                      <span className="text-[10px] text-stone-500">{new Date(c.created_at).toLocaleString("es-ES")}</span>
                      <button onClick={() => delComment(c.id)} className="ml-auto text-stone-600 hover:text-red-400"><Trash2 className="w-3 h-3" /></button>
                    </div>
                    <div className="text-sm text-stone-200 whitespace-pre-wrap">{c.text}</div>
                  </div>
                ))}
                {(card.comments || []).length === 0 && <div className="text-xs text-stone-600 italic">Sin comentarios</div>}
              </div>
              <div className="flex gap-2">
                <textarea value={comment} onChange={(e) => setComment(e.target.value)}
                  rows={2} className="field text-sm resize-none flex-1"
                  placeholder="Añadir comentario..." data-testid="comment-input" />
                <button onClick={submitComment} className="bg-[#E0651F] hover:bg-[#ff7a30] text-white px-4 py-2 text-xs uppercase tracking-[0.15em] font-bold rounded-sm self-end inline-flex items-center gap-1.5" data-testid="comment-submit">
                  <MessageSquare className="w-3.5 h-3.5" /> Añadir
                </button>
              </div>
            </Section>

            <Section title={`Adjuntos (${(card.attachments || []).length})`}>
              <div className="space-y-2 mb-3">
                {(card.attachments || []).map((a) => (
                  <div key={a.id} className="bg-stone-900 border border-stone-800 p-3 rounded-sm flex items-center gap-3" data-testid={`attachment-${a.id}`}>
                    <Paperclip className="w-4 h-4 text-[#E0651F] flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-white truncate">{a.filename}</div>
                      <div className="text-[10px] text-stone-500">{(a.size / 1024).toFixed(1)} KB · subido {new Date(a.uploaded_at).toLocaleString("es-ES")}</div>
                    </div>
                    <button onClick={() => downloadAttachment(a.id)} className="text-stone-400 hover:text-[#E0651F]" title="Descargar"><Download className="w-4 h-4" /></button>
                    <button onClick={() => delAttachment(a.id)} className="text-stone-600 hover:text-red-400" title="Eliminar"><Trash2 className="w-4 h-4" /></button>
                  </div>
                ))}
                {(card.attachments || []).length === 0 && <div className="text-xs text-stone-600 italic">Sin adjuntos</div>}
              </div>
              <label className="inline-flex items-center gap-2 bg-stone-800 hover:bg-stone-700 border border-stone-700 px-4 py-2 text-xs uppercase tracking-[0.15em] font-bold rounded-sm cursor-pointer" data-testid="attachment-upload">
                <input ref={fileInputRef} type="file" className="hidden" onChange={(e) => uploadFile(e.target.files?.[0])} />
                <Paperclip className="w-3.5 h-3.5" /> {uploading ? "Subiendo…" : "Subir archivo (máx 8 MB)"}
              </label>
            </Section>

            <Section title={`Alertas programadas (${(card.alertas || []).filter((a) => !a.enviado).length} pendientes)`}>
              <AlertasBlock alertas={card.alertas || []} onAdd={addAlerta} onDelete={delAlerta} />
            </Section>

            <div className="text-[10px] text-stone-600 font-mono pt-4 border-t border-stone-800">
              Origen: {card.source} {card.source_ref_id ? `· ref ${card.source_ref_id.slice(0, 8)}` : ""} · Creado {new Date(card.created_at).toLocaleString("es-ES")} · Actualizado {new Date(card.updated_at).toLocaleString("es-ES")}
            </div>
          </div>
        </div>
      </main>

      <style>{`
        .field {
          width: 100%;
          background: #1c1917;
          border: 1px solid #44403c;
          color: #f5f5f4;
          padding: 0.45rem 0.7rem;
          font-size: 0.85rem;
          border-radius: 2px;
          outline: none;
          transition: border-color .15s;
        }
        .field:focus { border-color: #E0651F; }
      `}</style>
    </div>
  );
}

function PropRow({ icon, label, children }) {
  return (
    <div>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] font-bold text-stone-500 mb-1.5">
        {icon}{label}
      </div>
      {children}
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="bg-stone-900 border border-stone-800 p-5 rounded-sm">
      <h2 className="font-display font-black text-sm tracking-[0.18em] uppercase text-[#E0651F] mb-4">{title}</h2>
      {children}
    </div>
  );
}

function AlertasBlock({ alertas, onAdd, onDelete }) {
  const [dt, setDt] = useState("");
  const [msg, setMsg] = useState("");
  const sorted = [...alertas].sort((a, b) => new Date(a.fecha_hora) - new Date(b.fecha_hora));
  const fmt = (iso) => {
    try { return new Date(iso).toLocaleString("es-ES", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
    catch { return iso; }
  };
  const minDt = (() => {
    const d = new Date(Date.now() + 60000);
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  })();
  return (
    <div>
      <div className="space-y-2 mb-4">
        {sorted.length === 0 && <div className="text-xs text-stone-600 italic">Sin alertas programadas. Crea una abajo para recibir un email recordatorio.</div>}
        {sorted.map((a) => {
          const isPast = new Date(a.fecha_hora) < new Date();
          return (
            <div key={a.id} className={`flex items-center gap-3 p-3 rounded-sm border ${a.enviado ? "bg-stone-900 border-stone-800 opacity-60" : isPast ? "bg-red-900/20 border-red-700/40" : "bg-stone-900 border-[#E0651F]/30"}`} data-testid={`alerta-${a.id}`}>
              {a.enviado ? <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0" /> : <BellRing className={`w-4 h-4 flex-shrink-0 ${isPast ? "text-red-400" : "text-[#E0651F]"}`} />}
              <div className="flex-1 min-w-0">
                <div className={`text-sm ${a.enviado ? "line-through text-stone-500" : "text-white"}`}>{fmt(a.fecha_hora)}</div>
                {a.mensaje && <div className="text-xs text-stone-400 truncate">{a.mensaje}</div>}
                {a.enviado && a.enviado_at && <div className="text-[10px] text-emerald-500">Email enviado · {fmt(a.enviado_at)}</div>}
              </div>
              <button onClick={() => onDelete(a.id)} className="text-stone-500 hover:text-red-400 p-1" title="Eliminar alerta">
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          );
        })}
      </div>
      <div className="border-t border-stone-800 pt-4">
        <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-stone-500 mb-2">Programar nueva alerta</div>
        <div className="grid sm:grid-cols-[1fr_2fr_auto] gap-2">
          <input type="datetime-local" value={dt} onChange={(e) => setDt(e.target.value)} min={minDt}
            className="field" data-testid="alerta-datetime" />
          <input value={msg} onChange={(e) => setMsg(e.target.value)} placeholder="Mensaje (opcional, p.ej. 'Llamar para confirmar pago')"
            className="field" data-testid="alerta-message" />
          <button onClick={() => { if (dt) { onAdd(dt, msg); setDt(""); setMsg(""); } }}
            disabled={!dt}
            data-testid="alerta-add"
            className="inline-flex items-center gap-1 bg-[#E0651F] hover:bg-[#ff7a30] disabled:opacity-40 text-white px-4 py-2 text-xs uppercase tracking-[0.15em] font-bold rounded-sm">
            <Bell className="w-3.5 h-3.5" /> Añadir
          </button>
        </div>
        <div className="text-[10px] text-stone-500 mt-2">📧 Recibirás un email automático a la hora exacta con el título de esta tarjeta.</div>
      </div>
    </div>
  );
}
